/* eslint-disable no-console */
import { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import useTranslation from 'Hooks/useTranslation';
import { useLocation } from 'react-router-dom';
import Button from '@mui/material/Button';
import { S3_IMAGES, TERMINAL_BASE } from 'Constants/General';
import { colors } from 'Constants/Colors';
import { TextField, FormHelperText, Typography, Select, MenuItem, Tooltip } from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Modal from 'Components/Modal';
import Alert from 'Components/Alert';
import SelectInput from 'Components/FormComponents/SelectInput';
import CustomDatePicker from 'Components/CustomDatePicker/CustomDatePicker';
import FormValidations from 'Utils/FormValidations';
import { s3 } from 'Services/AWS/s3';
import SaveInfoModal from './Components/SaveInfoModal';
import useFetch from './Hooks/useFetch';
import useModal from './Hooks/useModal';
import {
  hr,
  mainContainer,
  fieldsContainer,
  buttonStyle,
  buttonsContainer,
  InputContainer,
  ColumnContainer,
  textInputStyle,
  ImageUploadContainer,
  redAsterix,
} from './style';

import ImageUploadComponent from './Components/ImageUpload';

export default function EditCourseInformation({ handleNameChange }) {
  const { t2 } = useTranslation('EditCourse');
  const location = useLocation();
  const courseId = location?.state?.course[(location?.state?.course?.length || 0) - 1];
  const [open, setOpen] = useState(false);
  const [unchanged, setUnchanged] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState();
  const [formData, setFormData] = useState({ expirationDate: null });
  const { openModal, setOpenModal } = useModal();

  const { course, categories, priorities, setCourseBasicInformation } = useFetch(courseId);
  const [days, setDays] = useState('');
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');

  const expirationTime = [
    { id: 0, name: t2('Never') },
    { id: 3, name: `3 ${t2('Months')}` },
    { id: 6, name: `6 ${t2('Months')}` },
    { id: 12, name: `12 ${t2('Months')}` },
    { id: 18, name: `18 ${t2('Months')}` },
  ];

  useEffect(() => {
    if (!formData.name && course.name) {
      // Logic to convert float trainingHours to days, hours, minutes
      // 1 Day = 8 Hours
      const totalHours = parseFloat(course.trainingHours) || 0;

      const d = Math.floor(totalHours / 8);
      const remainingHours = totalHours % 8;
      const h = Math.floor(remainingHours);
      const m = Math.round((remainingHours - h) * 60);

      setFormData({
        ...course,
        expirationDate: course.expirationDate ? DateTime.fromISO(course.expirationDate) : null,
      });
      setDays(d.toString());
      setHours(h.toString());
      setMinutes(m.toString());
      setIsLoading(false);
    }
  }, [course, formData]);

  const handleChange = event => {
    setUnchanged(false);
    if (event.target.name === 'courseName') {
      setFormData({ ...formData, name: event.target.value });
      return null;
    }

    if (event.target.name === 'trainingHoursDays') {
      setDays(event.target.value);
      return null;
    }
    if (event.target.name === 'trainingHoursHours') {
      const value = event.target.value;
      if (value === '' || (Number(value) >= 0 && Number(value) <= 99)) {
        setHours(value);
      }
      return null;
    }
    if (event.target.name === 'trainingHoursMinutes') {
      setMinutes(event.target.value);
      return null;
    }

    setFormData({
      ...formData,
      [event.target.name]: event.target.value,
    });
    return null;
  };

  const handleExpirationDateChange = date => {
    setUnchanged(false);
    setFormData({
      ...formData,
      expirationDate: date,
    });
  };

  const handleImageUpload = file => {
    setUnchanged(false);
    const extension = file.name.split('.').slice(-1)[0];
    const fileData = {
      file,
      extension,
    };
    setFormData({
      ...formData,
      thumbnail: fileData,
    });
  };

  const handleSubmit = async () => {
    let key = formData.thumbnail;
    const { file, extension } = formData.thumbnail;

    const isFileUploaded = file !== undefined && extension !== undefined;

    if (isFileUploaded === true) {
      try {
        key = `${S3_IMAGES}/${formData.id}/CourseImage.${extension}`;
        await s3.uploadFileToBucketWithKeyProvided(key, file, file.type || 'image/jpeg');
      } catch (caught) {
        console.error(`Error ${caught}`);
        return false;
      }
    }

    // Calculate total hours as float
    const d = parseInt(days || '0', 10);
    const h = parseInt(hours || '0', 10);
    const m = parseInt(minutes || '0', 10);

    const totalHours = d * 8 + h + m / 60;

    const response = await setCourseBasicInformation({
      ...formData,
      expirationDate: formData.expirationDate ? formData.expirationDate.toFormat('yyyy-MM-dd') : null,
      thumbnail: key,
      trainingHours: totalHours,
      terminals: [TERMINAL_BASE],
    });

    if (response?.id) {
      handleNameChange(formData.name);
      setOpen(true);
    }

    setOpenModal(false);
    return true;
  };

  const validate = () => {
    const temp = {};

    temp.courseName = formData.name.length >= 3 ? '' : t2('FormCourseNameError');
    temp.category = formData.category ? '' : t2('FormCourseCategoryError');
    temp.priority = formData.priority ? '' : t2('FormCoursePriorityError');
    // temp.terminals =
    //   formData.terminals.length !== 0 ? '' : t2('FormCourseTerminalError');
    temp.expirationTime = formData.expirationTime !== '' ? '' : t2('FormCourseExpirationTimeError');

    temp.expirationDate = formData.expirationDate
      ? FormValidations.dateIsFuture(formData.expirationDate.toISO())
        ? ''
        : 'La fecha de vencimiento debe ser hoy o una fecha futura.'
      : '';

    temp.description = FormValidations.courseDescription(formData?.description);

    setErrors({
      ...temp,
    });

    return Object.values(temp).every(x => x === '');
  };

  const submitHandler = e => {
    e.preventDefault();
    const isValid = validate();

    if (isValid) {
      setOpenModal(true);
    }
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  if (isLoading) {
    return <div>{t2('Loading')}</div>;
  }

  const asterix = <div style={redAsterix}>*</div>;
  const today = new Date();
  today.setMonth(today.getMonth() + 5);

  return (
    <div className="col" style={mainContainer}>
      <h3>{t2('BasicInformation')}</h3>
      <hr style={hr} />
      <form className="col" onSubmit={e => submitHandler(e)}>
        <div style={fieldsContainer}>
          <ColumnContainer>
            <InputContainer htmlFor="courseName">
              <div>
                {t2('CourseName')} {asterix}
              </div>
              <TextField
                name="courseName"
                placeholder={t2('CourseName')}
                value={formData?.name || ''}
                onChange={handleChange}
                type="text"
                color="success"
                sx={{
                  input: { ...textInputStyle },
                }}
                {...(errors?.courseName && {
                  error: true,
                  label: 'Error',
                  helperText: errors.courseName,
                })}
              />
            </InputContainer>
            <InputContainer htmlFor="category">
              <div>
                {t2('Category')} {asterix}
              </div>
              {categories.length > 0 && (
                <SelectInput
                  name="category"
                  placeholder={t2('Category')}
                  value={formData?.category || ''}
                  onChange={handleChange}
                  sx={{ backgroundColor: colors.WHITE }}
                  disableunderline="true"
                  options={categories}
                  errortext={errors?.category}
                />
              )}
            </InputContainer>
            {/* <InputContainer htmlFor="terminal">
              <div>
                {t2('Terminal')} {asterix}
              </div>
              {terminals.length > 0 && formData?.terminals && (
                <MultiSelectInput
                  name="terminal"
                  data={terminals}
                  selected={formData?.terminals || []}
                  onChange={handleTerminalsSelect}
                  placeHolderText={t2('Terminal')}
                />
              )}
              {errors?.terminals && (
                <FormHelperText sx={{ color: colors.RED }}>
                  {errors.terminals}
                </FormHelperText>
              )}
            </InputContainer> */}
            <InputContainer htmlFor="description">
              <div
                style={{
                  display: 'inline-flex',
                  width: '100%',
                  justifyContent: 'space-between',
                }}
              >
                {t2('Description')}
                {formData?.description?.length > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: formData?.description?.length > 255 ? colors.RED : colors.GRAY,
                    }}
                  >
                    {`${formData?.description?.length}/255`}
                  </Typography>
                )}
              </div>
              <TextField
                name="description"
                placeholder={t2('Description')}
                multiline
                minRows={4}
                maxRows={4}
                value={formData?.description || ''}
                onChange={handleChange}
                sx={{
                  '& .MuiInputBase-root': {
                    backgroundColor: colors.WHITE,
                  },
                }}
                InputProps={{ disableunderline: 'true' }}
                color="success"
                {...(errors?.description && {
                  error: true,
                  label: 'Error',
                  helperText: errors.description,
                })}
              />
            </InputContainer>
            <InputContainer htmlFor="trainingHours">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                {t2('training_hours')}
                <Tooltip title={t2('days_tooltip')} arrow placement="top">
                  <InfoOutlined sx={{ fontSize: '1rem', color: colors.GREEN, cursor: 'pointer' }} />
                </Tooltip>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                {/* Days Input */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <TextField
                    name="trainingHoursDays"
                    placeholder={'0'}
                    value={days}
                    onChange={handleChange}
                    type="number"
                    inputProps={{ min: 0 }}
                    sx={{
                      '& .MuiInputBase-root': {
                        backgroundColor: colors.WHITE,
                      },
                    }}
                    style={{ width: '4.1rem' }}
                    InputProps={{ disableunderline: 'true' }}
                    color="success"
                  />
                  <Typography variant="caption">{t2('Days')}</Typography>
                </div>

                <span style={{ fontStyle: 'bold', marginBottom: '1.2rem' }}>:</span>

                {/* Hours Input */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <TextField
                    name="trainingHoursHours"
                    placeholder={'0'}
                    value={hours}
                    onChange={handleChange}
                    type="number"
                    inputProps={{ min: 0, max: 99 }}
                    sx={{
                      '& .MuiInputBase-root': {
                        backgroundColor: colors.WHITE,
                      },
                    }}
                    style={{ width: '4.1rem' }}
                    InputProps={{ disableunderline: 'true' }}
                    color="success"
                  />
                  <Typography variant="caption">{t2('Hours')}</Typography>
                </div>

                <span style={{ fontStyle: 'bold', marginBottom: '1.2rem' }}>:</span>

                {/* Minutes Input */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <TextField
                    name="trainingHoursMinutes"
                    placeholder={'0'}
                    value={minutes}
                    onChange={handleChange}
                    type="number"
                    inputProps={{ min: 0, max: 59 }}
                    sx={{
                      '& .MuiInputBase-root': {
                        backgroundColor: colors.WHITE,
                      },
                    }}
                    style={{ width: '4.1rem' }}
                    InputProps={{ disableunderline: 'true' }}
                    color="success"
                  />
                  <Typography variant="caption">{t2('Minutes')}</Typography>
                </div>
              </div>
            </InputContainer>
          </ColumnContainer>
          <ColumnContainer>
            <InputContainer htmlFor="priority">
              <div>
                {t2('CoursePriority')} {asterix}
              </div>
              {priorities?.length > 0 && (
                <SelectInput
                  name="priority"
                  placeholder={t2('Priority')}
                  value={formData?.priority || ''}
                  onChange={handleChange}
                  sx={{ backgroundColor: colors.WHITE }}
                  disableunderline="true"
                  options={priorities}
                  errortext={errors?.priority}
                />
              )}
            </InputContainer>
            <InputContainer htmlFor="expirationTime">
              <div>
                {t2('ExpirationTime')} {asterix}
              </div>
              {expirationTime.length > 0 && (
                <SelectInput
                  name="expirationTime"
                  placeholder={t2('ExpirationTime')}
                  value={formData?.expirationTime}
                  onChange={handleChange}
                  sx={{ backgroundColor: colors.WHITE }}
                  disableunderline="true"
                  options={expirationTime}
                />
              )}
              {errors?.expirationTime && (
                <FormHelperText sx={{ color: colors.RED }}>{errors.expirationTime}</FormHelperText>
              )}
            </InputContainer>
            <InputContainer htmlFor="expirationDate">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                Fecha de vencimiento del curso
                <Tooltip
                  title="Define una fecha límite para la disponibilidad del curso. Los cursos que superan esta fecha pasan automáticamente a estado borrador."
                  arrow
                  placement="top"
                >
                  <InfoOutlined sx={{ fontSize: '1rem', color: colors.GREEN, cursor: 'pointer' }} />
                </Tooltip>
              </div>
              <CustomDatePicker
                label=""
                placeholder="Sin vencimiento"
                value={formData.expirationDate}
                onChange={handleExpirationDateChange}
                fullWidth
              />
              {errors?.expirationDate && (
                <FormHelperText id="expirationDate-error" sx={{ color: colors.RED }}>
                  {errors.expirationDate}
                </FormHelperText>
              )}
            </InputContainer>
            <ImageUploadContainer>
              {t2('Thumbnail')}
              <ImageUploadComponent callback={handleImageUpload} />
            </ImageUploadContainer>
          </ColumnContainer>
        </div>
        <div style={buttonsContainer}>
          <Button
            variant="contained"
            style={unchanged ? buttonStyle(true) : buttonStyle(false)}
            type="submit"
            disabled={unchanged}
          >
            {t2('Save')}
          </Button>
        </div>
        <Modal
          open={openModal}
          setOpen={setOpenModal}
          Children={
            <SaveInfoModal
              accept={handleSubmit}
              cancel={handleClose}
              title={t2('BasicInfoModalTitle')}
              message={t2('BasicInfoModalMessage')}
            />
          }
        />
        <Alert message={t2('BasicInfoAlertSuccess')} open={open} setOpen={setOpen} />
      </form>
    </div>
  );
}
